import { createBuilderNode } from './builderNodes';

export const PAGE_TEMPLATE_TYPES = Object.freeze({
  HOME: 'home',
  ABOUT: 'about',
  SERVICES: 'services',
  CONTACT: 'contact',
  PROMO_PROGRAMS: 'promo-programs',
  PRINT_SERVICES: 'print-services',
  PORTFOLIO: 'portfolio',
});

export const PAGE_TEMPLATE_IMPORT_MODES = Object.freeze({
  REPLACE: 'replace',
  APPEND: 'append',
});

const widget = (component, props = {}) => ({ component, version: 1, props });

const hero = (title, subtitle, buttonText = '', buttonUrl = '', options = {}) => widget('hero', {
  title,
  subtitle,
  min_height: options.min_height || 520,
  autoplay: false,
  slides: [{
    background_url: options.background_url || '',
    h1: title,
    paragraph: subtitle,
    button_text: buttonText,
    button_url: buttonUrl,
    background_overlay_opacity: options.overlay ?? 0.45,
  }],
});

const cards = ({ subheading = '', title, description = '', items, columns = 3, style = 'elevated', variant = 'services' }) => widget('cards_grid', {
  subheading, title, description, variant,
  columns_desktop: columns,
  columns_tablet: Math.min(columns, 2),
  columns_mobile: 1,
  card_style: style,
  items,
});

const contentMedia = ({ subheading = '', title, body, ctaLabel = '', ctaUrl = '', position = 'right', imageAlt = '' }) => widget('rich_text', {
  subheading, title, body,
  cta_label: ctaLabel,
  cta_url: ctaUrl,
  image_url: '',
  image_alt: imageAlt || title,
  media_position: position,
  vertical_align: 'center',
  content_width: 48,
  gap: 52,
  image_radius: 20,
  image_shadow: 'medium',
  image_aspect: '4/3',
});

const stats = (title, items) => widget('stats', {
  title,
  columns_desktop: Math.min(Math.max(items.length, 2), 4),
  columns_tablet: 2,
  columns_mobile: 2,
  items,
});

const testimonials = (title = 'What customers say') => widget('testimonials', {
  subheading: 'Customer stories',
  title,
  columns_desktop: 3,
  columns_tablet: 2,
  columns_mobile: 1,
  items: [
    { quote: 'Add a real customer outcome here.', name: 'Customer name', role: 'Company', image_url: '', rating: 5 },
    { quote: 'Use a quote that explains the experience of working with your team.', name: 'Customer name', role: 'Company', image_url: '', rating: 5 },
    { quote: 'Specific proof is more persuasive than generic praise.', name: 'Customer name', role: 'Company', image_url: '', rating: 5 },
  ],
});

const faq = (title, items) => widget('faq', {
  subheading: 'Common questions',
  title,
  style: 'joined',
  allow_multiple: false,
  open_first: true,
  items,
});

const cta = (title, body, primaryLabel = 'Start a project', primaryUrl = '/contact', secondaryLabel = '', secondaryUrl = '') => widget('cta', {
  subheading: 'Ready when you are',
  title,
  body,
  primary_label: primaryLabel,
  primary_url: primaryUrl,
  secondary_label: secondaryLabel,
  secondary_url: secondaryUrl,
  align: 'center',
  style: 'panel',
});

const gallery = (title = 'See what is possible') => widget('gallery', {
  subheading: 'Recent work',
  title,
  description: 'Replace these placeholders with real projects, campaign photos, print samples or branded merchandise.',
  layout: 'grid',
  columns_desktop: 3,
  columns_tablet: 2,
  columns_mobile: 1,
  gap: 16,
  image_radius: 18,
  lightbox: true,
  items: [
    { image_url: '', alt: 'Project example one', caption: 'Project one', link_url: '' },
    { image_url: '', alt: 'Project example two', caption: 'Project two', link_url: '' },
    { image_url: '', alt: 'Project example three', caption: 'Project three', link_url: '' },
  ],
});

const form = (title = 'Tell us about your project') => widget('form', {
  form_key: '',
  title,
  description: 'Share the basics and our team can help shape the right solution.',
  submit_label: 'Send project details',
  success_message: 'Thanks — your request has been received.',
  email_from: 'info@elitecore.ca',
  email_from_name: 'Elite Core',
  email_to: '',
  email_cc: '',
  email_bcc: '',
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'email', label: 'Email', type: 'email', required: true },
    { key: 'company', label: 'Company', type: 'text', required: false },
    { key: 'quantity', label: 'Estimated quantity', type: 'text', required: false },
    { key: 'target_date', label: 'Target date', type: 'text', required: false },
    { key: 'message', label: 'What are you planning?', type: 'textarea', required: true },
  ],
});

const comparison = () => widget('comparison', {
  subheading: 'Decoration guide',
  title: 'Choose the right decoration method',
  description: 'A quick starting point. Your team can tailor the recommendation to the garment, artwork, quantity and budget.',
  first_column_label: 'Consideration',
  columns: [
    { key: 'embroidery', label: 'Embroidery', highlight: false },
    { key: 'screenprint', label: 'Screen Print', highlight: true },
    { key: 'transfer', label: 'DTF / Transfer', highlight: false },
  ],
  rows: [
    { label: 'Best for', embroidery: 'Hats, polos, jackets', screenprint: 'Tees, hoodies, volume runs', transfer: 'Detailed art, short runs' },
    { label: 'Look', embroidery: 'Premium / dimensional', screenprint: 'Classic / durable', transfer: 'Detailed / full colour' },
    { label: 'Typical quantity', embroidery: 'Low to high', screenprint: 'Medium to high', transfer: 'Low to medium' },
  ],
  cta_label: 'Ask what fits your project',
  cta_url: '/contact',
});

const timeline = () => widget('timeline', {
  subheading: 'How it works',
  title: 'A clear path from idea to delivery',
  description: '',
  style: 'vertical',
  items: [
    { label: '01', title: 'Discover', body: 'Define the audience, objective, quantity, budget and deadline.' },
    { label: '02', title: 'Create', body: 'Curate products or print solutions and prepare the creative direction.' },
    { label: '03', title: 'Approve', body: 'Review artwork, proofs, specifications and production details.' },
    { label: '04', title: 'Produce & Deliver', body: 'Coordinate production, fulfillment, shipping or installation.' },
  ],
});

const team = () => widget('team', {
  subheading: 'Your team',
  title: 'Real people behind every project',
  description: 'Introduce the people customers will actually work with.',
  columns_desktop: 3,
  columns_tablet: 2,
  columns_mobile: 1,
  card_style: 'cards',
  items: [
    { name: 'Account Manager', role: 'Sales & Strategy', bio: 'Your main partner for planning, ideas and project coordination.', image_url: '', email: '', linkedin_url: '' },
    { name: 'Creative Lead', role: 'Design & Artwork', bio: 'Turns concepts into production-ready branded creative.', image_url: '', email: '', linkedin_url: '' },
    { name: 'Production Lead', role: 'Production & Fulfillment', bio: 'Keeps production, quality and delivery moving.', image_url: '', email: '', linkedin_url: '' },
  ],
});

const productGrid = (title = 'Popular products', size = 12) => widget('product_grid', {
  title,
  page_size: size,
  columns_desktop: 4,
  columns_tablet: 3,
  columns_mobile: 2,
  query_source: 'all',
  query_supplier: '',
  query_search: '',
  query_product_ids: '',
  query_sort: 'default',
});

const categoryGrid = (heading = 'Browse categories') => widget('category_grid', {
  heading,
  taxonomy: 'product_category',
  limit: 12,
  columns_desktop: 6,
  columns_tablet: 3,
  columns_mobile: 2,
  source: 'archive',
});

const TEMPLATE_DEFINITIONS = [
  {
    id: 'home-editorial-commerce',
    type: PAGE_TEMPLATE_TYPES.HOME,
    name: 'Promo Distributor — Modern Home',
    description: 'A complete modern distributor home page with services, categories, proof, work examples and conversion.',
    tags: ['promo', 'distributor', 'modern', 'commerce'],
    accent: 'Promo',
    widgets: [
      hero('Merchandise people actually want.', 'Strategic promotional products, apparel, print and fulfillment — built around your brand.', 'Start a project', '/contact'),
      cards({
        subheading: 'What we do',
        title: 'One partner from idea to delivery',
        description: 'Keep creative, sourcing, production and distribution connected.',
        items: [
          { icon: '01', title: 'Creative & Design', body: 'Campaign concepts, artwork and product direction.', link_label: '', link_url: '' },
          { icon: '02', title: 'Product Sourcing', body: 'Focused recommendations across merchandise, apparel and print.', link_label: '', link_url: '' },
          { icon: '03', title: 'Fulfillment', body: 'Kitting, inventory, shipping and distribution coordinated together.', link_label: '', link_url: '' },
        ],
      }),
      categoryGrid('Explore branded merchandise'),
      contentMedia({
        subheading: 'Company stores',
        title: 'Make repeat ordering ridiculously easy',
        body: 'Launch a branded storefront for employees, customers, events or ongoing programs while keeping approved products organized.',
        ctaLabel: 'Ask about company stores',
        ctaUrl: '/contact',
      }),
      stats('Built for real programs', [
        { value: '1', label: 'Connected partner' },
        { value: '24/7', label: 'Online ordering' },
        { value: 'Fast', label: 'Curated sourcing' },
        { value: 'End-to-end', label: 'Project visibility' },
      ]),
      gallery('Ideas, campaigns and branded experiences'),
      testimonials(),
      cta('Have a project in mind?', 'Tell us the audience, quantity, budget and target date. We can help figure out the rest.', 'Start a project', '/contact', 'Browse products', '/catalog'),
    ],
  },
  {
    id: 'home-corporate-store',
    type: PAGE_TEMPLATE_TYPES.HOME,
    name: 'Company Store — Modern Home',
    description: 'A storefront-focused home page for employee programs, company stores and recurring branded merchandise.',
    tags: ['company-store', 'employee', 'commerce', 'promo'],
    accent: 'Store',
    widgets: [
      hero('Your branded merchandise, always open.', 'Give employees, customers and teams one easy place to order approved products.', 'Shop the store', '/catalog'),
      categoryGrid('Start with what you need'),
      cards({
        subheading: 'Built for programs',
        title: 'More than a product catalogue',
        items: [
          { icon: '01', title: 'Employee Apparel', body: 'Approved teamwear and uniforms that stay consistent.' },
          { icon: '02', title: 'Onboarding & Recognition', body: 'Kits and gifts for new hires, milestones and culture.' },
          { icon: '03', title: 'Events & Campaigns', body: 'Focused collections for conferences, launches and team moments.' },
        ],
      }),
      productGrid('Popular picks', 12),
      contentMedia({
        subheading: 'Easy for shoppers',
        title: 'Simple on the front. Organized behind the scenes.',
        body: 'Create a polished branded experience while your team controls approved items, program rules and fulfillment.',
        position: 'left',
      }),
      faq('Store questions', [
        { question: 'Who can order?', answer: 'Explain access, eligibility or login requirements.' },
        { question: 'How does shipping work?', answer: 'Explain production, fulfillment, pickup or shipping expectations.' },
        { question: 'Need help with an order?', answer: 'Point shoppers to the right support contact.' },
      ]),
      cta('Need help finding the right item?', 'Give shoppers an easy path to support without cluttering the store.', 'Contact support', '/contact'),
    ],
  },
  {
    id: 'home-print-shop',
    type: PAGE_TEMPLATE_TYPES.HOME,
    name: 'Print Shop — Modern Home',
    description: 'A conversion-focused home page for commercial print, signage, apparel and branded production.',
    tags: ['print', 'signage', 'commercial-print', 'local'],
    accent: 'Print',
    widgets: [
      hero('Print that gets noticed.', 'Commercial print, signage, apparel and branded production with one team from file to finish.', 'Request a quote', '/contact'),
      cards({
        subheading: 'Print capabilities',
        title: 'From quick runs to full campaigns',
        columns: 4,
        style: 'outlined',
        items: [
          { icon: '01', title: 'Commercial Print', body: 'Brochures, booklets, cards, sales materials and everyday print.' },
          { icon: '02', title: 'Signs & Displays', body: 'Banners, boards, event graphics, retail displays and signage.' },
          { icon: '03', title: 'Labels & Packaging', body: 'Labels, stickers, packaging components and finishing.' },
          { icon: '04', title: 'Apparel & Promo', body: 'Branded apparel and merchandise to complete the campaign.' },
        ],
      }),
      contentMedia({
        subheading: 'Production support',
        title: 'Not sure what stock, size or finish you need?',
        body: 'Bring us the goal and the artwork. We can help choose materials, finishing and production methods that fit the application and budget.',
        ctaLabel: 'Talk to a print expert',
        ctaUrl: '/contact',
      }),
      gallery('Recent print and signage work'),
      timeline(),
      testimonials('Trusted for deadlines that matter'),
      cta('Need pricing on a print project?', 'Send the size, quantity, artwork and deadline. We’ll help fill in the production details.', 'Request a print quote', '/contact'),
    ],
  },

  {
    id: 'about-brand-story',
    type: PAGE_TEMPLATE_TYPES.ABOUT,
    name: 'Distributor Story + Team',
    description: 'A modern About page combining brand story, values, people and credibility.',
    tags: ['about', 'promo', 'team', 'story'],
    accent: 'Story',
    widgets: [
      hero('More than merchandise.', 'We help brands turn products, print and apparel into experiences people remember.'),
      contentMedia({
        subheading: 'Our story',
        title: 'Built around relationships, not transactions',
        body: 'Use this section for your origin, your point of view and why customers choose to keep working with your team.',
        position: 'left',
      }),
      cards({
        subheading: 'What guides us',
        title: 'A better standard for branded merchandise',
        items: [
          { icon: '01', title: 'Thoughtful', body: 'Recommend what fits the audience and objective — not just what is available.' },
          { icon: '02', title: 'Reliable', body: 'Communicate clearly and keep the details moving.' },
          { icon: '03', title: 'Human', body: 'Technology should make service easier, not replace the relationship.' },
        ],
        style: 'minimal',
      }),
      team(),
      stats('The experience we aim for', [
        { value: 'Clear', label: 'Communication' },
        { value: 'Useful', label: 'Recommendations' },
        { value: 'Reliable', label: 'Execution' },
      ]),
      cta('Ready to meet the team?', 'Start with a project or a simple question.', 'Contact us', '/contact'),
    ],
  },
  {
    id: 'about-print-shop',
    type: PAGE_TEMPLATE_TYPES.ABOUT,
    name: 'Print Shop Story + Capabilities',
    description: 'A production-focused About page for print shops, decorators and local manufacturers.',
    tags: ['about', 'print', 'production', 'local'],
    accent: 'Print',
    widgets: [
      hero('Craft, technology and people behind every job.', 'Tell the story of your shop, your production standards and the team customers trust.'),
      contentMedia({
        subheading: 'Our shop',
        title: 'Built to make good work easier to produce',
        body: 'Share your history, equipment, specialties and approach to quality without turning the page into a machine list.',
      }),
      stats('At a glance', [
        { value: 'Local', label: 'Production support' },
        { value: 'Fast', label: 'Turnaround options' },
        { value: 'Wide', label: 'Range of capabilities' },
        { value: 'Real', label: 'People to talk to' },
      ]),
      team(),
      gallery('Inside the shop'),
      cta('Bring us the next project.', 'We can help plan the production path before the file ever hits the press.', 'Request a quote', '/contact'),
    ],
  },

  {
    id: 'services-overview',
    type: PAGE_TEMPLATE_TYPES.SERVICES,
    name: 'Promo Services — Full Overview',
    description: 'A comprehensive promotional services page with capabilities, process, proof and inquiry.',
    tags: ['promo', 'services', 'capabilities'],
    accent: 'Promo',
    widgets: [
      hero('Everything you need to make branded merchandise work.', 'Strategy, creative, sourcing, production, stores and fulfillment with one connected partner.', 'Start a project', '/contact'),
      cards({
        subheading: 'Capabilities',
        title: 'Built around the way your program works',
        columns: 3,
        items: [
          { icon: '01', title: 'Strategy & Creative', body: 'Campaign direction, product ideas, artwork and brand support.' },
          { icon: '02', title: 'Merchandise & Apparel', body: 'Curated sourcing across categories, budgets and audiences.' },
          { icon: '03', title: 'Decoration & Production', body: 'Production methods selected around the product and artwork.' },
          { icon: '04', title: 'Company Stores', body: 'Branded storefronts for employees, clients and ongoing programs.' },
          { icon: '05', title: 'Kitting & Fulfillment', body: 'Assembly, inventory, split shipping and distribution.' },
          { icon: '06', title: 'Print & Packaging', body: 'Supporting print and packaging that completes the experience.' },
        ],
      }),
      timeline(),
      comparison(),
      gallery('Recent branded projects'),
      testimonials(),
      cta('Tell us what you are trying to accomplish.', 'You do not need to know the exact product yet.', 'Start a project', '/contact'),
    ],
  },
  {
    id: 'services-process',
    type: PAGE_TEMPLATE_TYPES.SERVICES,
    name: 'Promo Process + Lead Generation',
    description: 'A consultative service page focused on process, benefits, FAQs and project intake.',
    tags: ['promo', 'process', 'lead-gen'],
    accent: 'Process',
    widgets: [
      hero('A better process makes better merchandise.', 'Know what happens next from the first idea through final delivery.', 'Talk to our team', '/contact'),
      timeline(),
      cards({
        subheading: 'Why it works',
        title: 'Less friction at every stage',
        variant: 'benefits',
        items: [
          { icon: '01', title: 'Focused recommendations', body: 'Start with the audience and goal instead of endless catalogue browsing.' },
          { icon: '02', title: 'Clear approvals', body: 'Keep artwork, proofs and production details organized.' },
          { icon: '03', title: 'One accountable team', body: 'Reduce handoffs between sourcing, decoration and fulfillment.' },
        ],
        style: 'filled',
      }),
      faq('Before we get started', [
        { question: 'How early should we start?', answer: 'Earlier provides more product choices and room for artwork, production and shipping.' },
        { question: 'Can you help choose products?', answer: 'Yes. Share the audience, goal, quantity, budget and date.' },
        { question: 'Can you handle multiple locations?', answer: 'Programs can support bulk shipping, split shipping, kitting or individual distribution.' },
      ]),
      form('Start your next promotional project'),
    ],
  },

  {
    id: 'contact-simple',
    type: PAGE_TEMPLATE_TYPES.CONTACT,
    name: 'Project Inquiry + Form',
    description: 'A useful contact page with routing guidance, project intake fields and reassurance.',
    tags: ['contact', 'form', 'lead-gen', 'support'],
    accent: 'Contact',
    widgets: [
      hero('Let’s build something useful.', 'Tell us what you are planning — even if the product, print method or final specs are not figured out yet.'),
      cards({
        subheading: 'Start in the right place',
        title: 'How can we help?',
        columns: 2,
        style: 'outlined',
        items: [
          { icon: '01', title: 'New Project', body: 'Promotional products, apparel, print, events, stores and new programs.' },
          { icon: '02', title: 'Existing Order', body: 'Artwork, production, shipping, invoices or an existing project.' },
        ],
      }),
      form('Tell us about your project'),
      faq('Helpful before you send', [
        { question: 'What details are useful?', answer: 'Audience, quantity, budget, target date and any artwork or product ideas.' },
        { question: 'Do I need to know the exact product?', answer: 'No. A clear objective is enough to start.' },
      ]),
    ],
  },

  {
    id: 'promo-programs-company-stores',
    type: PAGE_TEMPLATE_TYPES.PROMO_PROGRAMS,
    name: 'Company Stores',
    description: 'A complete company-store sales page with use cases, benefits, process and CTA.',
    tags: ['promo', 'company-store', 'programs'],
    accent: 'Programs',
    widgets: [
      hero('Your branded merchandise, always open.', 'Create a company store for employees, customers, events or recurring programs.', 'Talk about your store', '/contact'),
      cards({
        subheading: 'Use cases',
        title: 'One storefront. A lot of possibilities.',
        columns: 4,
        style: 'outlined',
        items: [
          { icon: '01', title: 'Employee Apparel', body: 'Approved styles and easy repeat ordering.' },
          { icon: '02', title: 'Recognition', body: 'Milestone gifts and rewards without manual coordination.' },
          { icon: '03', title: 'Client Programs', body: 'Curated gifting and branded merchandise for customers.' },
          { icon: '04', title: 'Events', body: 'Temporary or ongoing stores around campaigns and events.' },
        ],
      }),
      contentMedia({
        subheading: 'Easy to shop',
        title: 'A storefront that still feels like your brand',
        body: 'Use curated collections, program rules and fulfillment options without making the customer experience feel like back-office software.',
      }),
      timeline(),
      faq('Company store questions', [
        { question: 'Can products be restricted?', answer: 'Programs can be built around approved products and collections.' },
        { question: 'Can stores support payments or quote requests?', answer: 'Store settings can support catalogue-only, quote-request or ecommerce experiences.' },
        { question: 'Can orders ship to individuals?', answer: 'Depending on the program, fulfillment can support individual or bulk destinations.' },
      ]),
      cta('Ready to launch a company store?', 'Start with the audience, product strategy and fulfillment requirements.', 'Plan a store', '/contact'),
    ],
  },
  {
    id: 'promo-programs-events',
    type: PAGE_TEMPLATE_TYPES.PROMO_PROGRAMS,
    name: 'Events + Campaign Merch',
    description: 'A campaign-focused page for conferences, launches, festivals and event merchandise.',
    tags: ['promo', 'events', 'campaigns'],
    accent: 'Events',
    widgets: [
      hero('Merchandise that makes the moment feel bigger.', 'Build the event experience from attendee kits and staff apparel to giveaways and signage.', 'Plan event merch', '/contact'),
      cards({
        subheading: 'Build the experience',
        title: 'Every touchpoint can feel connected',
        columns: 4,
        items: [
          { icon: '01', title: 'Attendee Kits', body: 'Welcome packs and pre-event mailers.' },
          { icon: '02', title: 'Staff Apparel', body: 'Consistent, comfortable apparel for the team.' },
          { icon: '03', title: 'Giveaways', body: 'Useful products matched to the audience.' },
          { icon: '04', title: 'Print & Signage', body: 'Supporting print, graphics and event materials.' },
        ],
      }),
      gallery('Event inspiration'),
      timeline(),
      cta('Working against an event date?', 'Share the date first. We can work backwards from the deadline.', 'Start planning', '/contact'),
    ],
  },

  {
    id: 'print-services-commercial',
    type: PAGE_TEMPLATE_TYPES.PRINT_SERVICES,
    name: 'Commercial Print',
    description: 'A complete page for brochures, booklets, cards, sell sheets and everyday commercial print.',
    tags: ['print', 'commercial-print', 'offset', 'digital'],
    accent: 'Print',
    widgets: [
      hero('Commercial print that shows up right.', 'From quick-turn digital work to larger production runs, get help with stock, finishing and delivery.', 'Request print pricing', '/contact'),
      cards({
        subheading: 'Popular print',
        title: 'Everyday print, professionally produced',
        columns: 3,
        items: [
          { icon: '01', title: 'Brochures & Sell Sheets', body: 'Marketing pieces, inserts and leave-behinds.' },
          { icon: '02', title: 'Booklets & Manuals', body: 'Multi-page pieces with binding and finishing options.' },
          { icon: '03', title: 'Cards & Stationery', body: 'Business cards, postcards, invitations and branded stationery.' },
        ],
      }),
      contentMedia({
        subheading: 'Production guidance',
        title: 'Stock and finishing should support the job',
        body: 'Explain coatings, folds, binding, specialty stocks or finishing without asking the customer to become a print expert.',
      }),
      gallery('Recent commercial print'),
      faq('Print questions', [
        { question: 'What file format should I send?', answer: 'Provide your preferred print-ready PDF and artwork requirements here.' },
        { question: 'Can you help with paper and finishing?', answer: 'Yes. Recommendations can be based on use, budget and desired feel.' },
        { question: 'Do you offer rush production?', answer: 'Explain rush availability and production constraints.' },
      ]),
      form('Request commercial print pricing'),
    ],
  },
  {
    id: 'print-services-signage',
    type: PAGE_TEMPLATE_TYPES.PRINT_SERVICES,
    name: 'Signs + Displays',
    description: 'A signage-focused page for banners, boards, event displays and environmental graphics.',
    tags: ['print', 'signage', 'wide-format', 'displays'],
    accent: 'Signage',
    widgets: [
      hero('Make the message hard to miss.', 'Banners, signs, displays and large-format graphics for events, retail and workplaces.', 'Start a signage project', '/contact'),
      cards({
        subheading: 'Large format',
        title: 'Built for spaces, not just pages',
        columns: 4,
        style: 'outlined',
        items: [
          { icon: '01', title: 'Banners', body: 'Indoor, outdoor and event banner solutions.' },
          { icon: '02', title: 'Rigid Signs', body: 'Boards, directional signs and temporary displays.' },
          { icon: '03', title: 'Event Displays', body: 'Backdrops, retractables and portable display systems.' },
          { icon: '04', title: 'Window & Wall Graphics', body: 'Branded environmental graphics and applications.' },
        ],
      }),
      gallery('Signs and displays in the real world'),
      contentMedia({
        subheading: 'Plan for the space',
        title: 'Size, material and installation all matter',
        body: 'Use this section to explain how your team helps choose materials and production based on viewing distance, environment and lifespan.',
        position: 'left',
      }),
      form('Tell us about the space'),
    ],
  },
  {
    id: 'print-services-apparel',
    type: PAGE_TEMPLATE_TYPES.PRINT_SERVICES,
    name: 'Apparel Decoration',
    description: 'A page for screen printing, embroidery, DTF/transfers and decorated apparel.',
    tags: ['print', 'apparel', 'embroidery', 'screen-print'],
    accent: 'Apparel',
    widgets: [
      hero('Apparel people will actually wear.', 'Choose the garment and decoration method together so the final result looks intentional.', 'Start an apparel project', '/contact'),
      comparison(),
      contentMedia({
        subheading: 'Garment + decoration',
        title: 'The method should fit the product',
        body: 'Embroidery, screen print and transfers each have strengths. Your team can recommend the best path based on art, quantity, placement and garment.',
      }),
      gallery('Recent apparel projects'),
      productGrid('Apparel inspiration', 12),
      form('Plan an apparel project'),
    ],
  },

  {
    id: 'portfolio-promo',
    type: PAGE_TEMPLATE_TYPES.PORTFOLIO,
    name: 'Promo Project Gallery',
    description: 'A visual portfolio page for promotional campaigns, kits, apparel and branded experiences.',
    tags: ['portfolio', 'promo', 'gallery'],
    accent: 'Work',
    widgets: [
      hero('Work worth showing off.', 'Use real project photography to show the range, quality and thinking behind your branded merchandise.'),
      gallery('Recent promotional projects'),
      cards({
        subheading: 'What you are seeing',
        title: 'Different goals need different solutions',
        style: 'minimal',
        items: [
          { icon: '01', title: 'Employee Programs', body: 'Apparel, onboarding and recognition.' },
          { icon: '02', title: 'Events', body: 'Kits, giveaways, staff gear and event graphics.' },
          { icon: '03', title: 'Client Campaigns', body: 'Gifting, direct mail and curated branded experiences.' },
        ],
      }),
      testimonials('What clients say about the work'),
      cta('Want to create the next one?', 'Bring us the objective and the audience.', 'Start a project', '/contact'),
    ],
  },
  {
    id: 'portfolio-print',
    type: PAGE_TEMPLATE_TYPES.PORTFOLIO,
    name: 'Print Shop Portfolio',
    description: 'A visual work page for commercial print, signage, packaging and production examples.',
    tags: ['portfolio', 'print', 'gallery'],
    accent: 'Print Work',
    widgets: [
      hero('See the work up close.', 'Show customers the quality, range and finishing your shop can produce.'),
      gallery('Recent print projects'),
      cards({
        subheading: 'Capabilities represented',
        title: 'Print across formats and environments',
        columns: 4,
        style: 'outlined',
        items: [
          { icon: '01', title: 'Commercial Print', body: 'Marketing collateral, booklets and everyday print.' },
          { icon: '02', title: 'Wide Format', body: 'Signs, boards, banners and display graphics.' },
          { icon: '03', title: 'Packaging & Labels', body: 'Labels, stickers and packaging components.' },
          { icon: '04', title: 'Apparel', body: 'Decorated garments and branded wearables.' },
        ],
      }),
      cta('Have something similar in mind?', 'Send the specs or even just a reference image.', 'Request a quote', '/contact'),
    ],
  },
];

export const PAGE_TEMPLATE_REGISTRY = Object.freeze(
  TEMPLATE_DEFINITIONS.reduce((registry, template) => {
    registry[template.id] = Object.freeze(template);
    return registry;
  }, {})
);

export const getPageTemplate = (id) => PAGE_TEMPLATE_REGISTRY[id] || null;

export const listPageTemplates = ({ type, query, tags = [] } = {}) => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  const requiredTags = (Array.isArray(tags) ? tags : []).map((tag) => String(tag).toLowerCase());

  return Object.values(PAGE_TEMPLATE_REGISTRY).filter((template) => {
    if (type && template.type !== type) return false;
    if (requiredTags.length && !requiredTags.every((tag) => template.tags.map((value) => value.toLowerCase()).includes(tag))) return false;
    if (!normalizedQuery) return true;
    return [template.name, template.description, template.type, ...(template.tags || [])]
      .join(' ')
      .toLowerCase()
      .includes(normalizedQuery);
  });
};

// Compatibility representation for the future tree-based builder tests.
// Live page imports use template.widgets directly through legacyPageTemplateAdapter.
const compatibilityNodeForWidget = (item, index) => ({
  type: 'core/section',
  props: { widthMode: 'contained' },
  style: {},
  children: [{
    type: 'core/text',
    props: {
      text: item?.props?.title || item?.props?.heading || item?.component || `Section ${index + 1}`,
    },
    style: {},
  }],
});

const instantiateNode = (node, idFactory) => {
  const children = Array.isArray(node.children) ? node.children.map((child) => instantiateNode(child, idFactory)) : [];
  return createBuilderNode(node.type, { ...node, children }, { idFactory });
};

export const instantiatePageTemplate = (templateId, { idFactory } = {}) => {
  const template = getPageTemplate(templateId);
  if (!template) throw new Error(`Unknown PromoShowcase page template: ${templateId}`);

  const compatibilityNodes = (template.widgets || []).map(compatibilityNodeForWidget);
  return {
    templateId: template.id,
    pageType: template.type,
    name: template.name,
    widgets: (template.widgets || []).map((item) => ({ ...item, props: { ...(item.props || {}) } })),
    nodes: compatibilityNodes.map((node) => instantiateNode(node, idFactory)),
  };
};

export const applyPageTemplateNodes = ({ existingNodes = [], templateNodes = [], mode = PAGE_TEMPLATE_IMPORT_MODES.REPLACE } = {}) => {
  if (mode === PAGE_TEMPLATE_IMPORT_MODES.APPEND) {
    return [...(Array.isArray(existingNodes) ? existingNodes : []), ...(Array.isArray(templateNodes) ? templateNodes : [])];
  }
  return Array.isArray(templateNodes) ? templateNodes : [];
};

export const duplicatePageNodes = (nodes = [], { idFactory } = {}) => {
  const cloneNode = (node) => instantiateNode({ ...node, id: undefined, children: (node.children || []).map((child) => ({ ...child, id: undefined })) }, idFactory);
  return (Array.isArray(nodes) ? nodes : []).map(cloneNode);
};
